/**
 * Tiny markdown helper — uses marked for parsing and DOMPurify for safe
 * rendering, with a constrained tag whitelist that's right-sized for
 * editorial story bodies.
 *
 * Supports: headings, paragraphs, bold/italic, links, lists, blockquotes,
 * code spans, line breaks. Blocks of code are allowed but not styled.
 *
 * NEVER renders raw HTML / scripts / iframes / event handlers, regardless
 * of what the author types — DOMPurify strips them.
 */
import { marked } from 'marked';
import DOMPurify from 'dompurify';

marked.setOptions({
  gfm: true,
  breaks: true,            // single newlines → <br>
  headerIds: false,
  mangle: false,
  smartypants: false,
});

const ALLOWED_TAGS = [
  'p', 'br', 'strong', 'em', 'u', 'a', 'code',
  'h1', 'h2', 'h3', 'h4',
  'ul', 'ol', 'li',
  'blockquote', 'hr',
];
const ALLOWED_ATTR = ['href', 'title', 'target', 'rel'];

/** Render markdown to a sanitised HTML string ready to drop into innerHTML. */
export function renderMarkdown(src) {
  if (!src) return '';
  const raw = marked.parse(src);
  // DOMPurify defaults are already safe; we further restrict to our subset.
  const clean = DOMPurify.sanitize(raw, {
    ALLOWED_TAGS, ALLOWED_ATTR, ALLOW_DATA_ATTR: false,
  });
  // Force every link to open in a new tab + add rel=noopener
  return clean.replace(/<a /g, '<a target="_blank" rel="noopener noreferrer" ');
}
